import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { SWRConfig } from "swr";
import type { Client, User } from "trailbase";
import { createClient, loginUrl } from "../data/client";
import { ManagementPage } from "../features/management/ManagementPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { SwipePage } from "../features/swipe/SwipePage";
import { AppShell, useCurrentPath } from "./AppShell";

type ClientState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly client: Client }
  | { readonly status: "error"; readonly error: Error };

export type Session = { readonly client: Client; readonly user: User };

const theme = createTheme({
  palette: {
    primary: { main: "#2457a6" },
    background: { default: "#f7f9fc" },
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: 'Inter, "Segoe UI", sans-serif' },
});

export const App = (): ReactElement => {
  const [state, setState] = useState<ClientState>({ status: "loading" });

  useEffect(() => {
    void createClient()
      .then((client) => setState({ status: "ready", client }))
      .catch((error: unknown) => {
        const normalized =
          error instanceof Error
            ? error
            : new Error("Unable to connect to Trailbase.");
        setState({ status: "error", error: normalized });
      });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {state.status === "loading" && <LoadingScreen />}
      {state.status === "error" && <ErrorScreen error={state.error} />}
      {state.status === "ready" && <AuthenticatedApp client={state.client} />}
    </ThemeProvider>
  );
};

const AuthenticatedApp = ({
  client,
}: {
  readonly client: Client;
}): ReactElement => {
  const user = client.user();
  if (user === undefined) {
    return <LoginScreen />;
  }
  const session: Session = { client, user };
  return (
    <SWRConfig value={{ revalidateOnFocus: false, shouldRetryOnError: false }}>
      <AppShell>
        <AppRoutes session={session} />
      </AppShell>
    </SWRConfig>
  );
};

const AppRoutes = ({
  session,
}: {
  readonly session: Session;
}): ReactElement => {
  const path = useCurrentPath();
  if (path === "/swipe") return <SwipePage session={session} />;
  if (path === "/settings") return <SettingsPage session={session} />;
  return <ManagementPage session={session} />;
};

const LoadingScreen = (): ReactElement => (
  <Container sx={{ py: 10, textAlign: "center" }}>
    <CircularProgress aria-label="Loading" />
  </Container>
);

const ErrorScreen = ({ error }: { readonly error: Error }): ReactElement => (
  <Container sx={{ py: 8 }}>
    <Alert severity="error">{error.message}</Alert>
  </Container>
);

const LoginScreen = (): ReactElement => (
  <Container maxWidth="sm" sx={{ py: { xs: 8, md: 14 } }}>
    <Paper sx={{ p: { xs: 3, sm: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" color="primary">
            A shared shortlist
          </Typography>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Baby Names
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Sign in to compare names privately with your partner.
          </Typography>
        </Box>
        <Button variant="contained" size="large" href={loginUrl()}>
          Sign in
        </Button>
      </Stack>
    </Paper>
  </Container>
);
